import Docker from 'dockerode';
import { Listr, ListrTask, ListrOptions } from 'listr2';
import chalk from 'chalk';
import yargs from 'yargs';
import { isEmpty, join } from 'lodash';
import path from 'upath';
import fs from 'fs-extra';

import logger from '@bmd-studio/genstack-logger';
import environment, { EnvVars, VersionedConfig } from '@bmd-studio/genstack-environment';

import { getCurrentServices, getRequestedServices } from './services';

export * from './docker';
export * from './services';

export interface ServiceRepository {
  url: string;
  ref?: string;
}

/**
 * Definition of a (micro) service.
 */
export interface Service {
  /** True when this service is active and should be deployed. */
  active?: boolean;
  /** Name of the service to identity it throughout the application. */
  name: string;
  /** Reference to the container to be used as a base for this service. */
  repository?: ServiceRepository;
  /** The relative path to the project-specific files for this service. */
  path?: string;
  /** Additional environment variables passed to this service. */
  env?: EnvVars;
}

/**
 * Versioned configuration of all the services for the application
 */
export interface VersionedServicesConfig extends Omit<VersionedConfig, 'config'> {
  /** Required config properties to define services */
  config: ServicesConfig;
}

/**
 * Definition of what properties are allowed in the `config` namespace of the service configuration. 
 */
export interface ServicesConfig {
  /** List of all the services */
  services: Service[];
}

/**
 * Definition of the possible environment variables for interfacing with the Docker deamon.
 */
export interface DockerEnv {
  /** App name prefix to be used by Docker Compose to identity this application. */
  COMPOSE_APP_NAME?: string;
  /** Project name prefix to be used by Docker Compose to identity this application. */
  COMPOSE_PROJECT_NAME?: string;
  /** The host name and port number concatenated with `:` in between
   of the Docker instance to deploy to. E.g. `docker.bmd:2376` */
  DOCKER_HOST?: string;
  /** Path to the SSL certificate for the Docker instance. */
  DOCKER_CERT_PATH?: string;
}

/**
 * Definition to match a container to a project service.
 */
export interface MatchedContainer {
  /** The container instance */
  container: Docker.ContainerInspectInfo;
  /** The project service definition */
  service: Service|undefined;
}

export interface LaunchConfig {
  configurations?: LaunchConfiguration[];
  compounds?: LaunchCompound[];
}

export interface LaunchConfiguration {
  type: string;
  request: string;
  name: string;
  protocol: string;
  port: number;
  restart: boolean;
  sourceMaps: boolean;
  localRoot: string;
  remoteRoot: string;
  outFiles: string[];
  skipFiles: string[];
}

export interface LaunchCompound {
  name: string;
  configurations: string[];
}

export interface TaskRunnerOptions {
  tasks: ListrTask[];
  preTasks?: ListrTask[];
  postTasks?: ListrTask[];
  taskOptions?: ListrOptions;
}

export type ServiceCommandHandler = (service: Service) => Promise<void>;

const ONE_SECOND = 1000;

export const createTaskRunner = ({ tasks, preTasks = [], postTasks = [], taskOptions = {} }: TaskRunnerOptions): Listr => {
  const envName = environment.getEnvName();
  const envType = environment.getEnvType();
  const genstackConfig = environment.getGenstackConfig();
  const projectRoot = environment.getProjectRoot();
  const envNameFilePath = path.join(projectRoot, genstackConfig?.environment?.envPath ?? '', envName); 
  const envNameFileExists = fs.existsSync(envNameFilePath);
  const services = getCurrentServices();
  const requestedServices = getRequestedServices();
  const serviceAmount = services.length;
  const taskRenderer = logger.isEnabled() ? 'verbose' : 'default';
  const defaultTaskOptions: ListrOptions = {
    // @ts-ignore
    renderer: taskRenderer,
    exitOnError: false,
    concurrent: true,
    rendererOptions: {
      collapse: false,
      collapseSkips: false,
      clearOutput: false,
      showSubtasks: true,
      showSkipMessage: true,
      collapseErrors: false,
      showErrorMessage: true,
      showTimer: true,
    },
  };

  // guard: make sure the environment name file exists
  if (!envNameFileExists) {
    throw new Error(`Could not find environment file ${chalk.underline(envName)} at ${chalk.underline(envNameFilePath)}. Make sure your environment exists.`);
  }

  const taskRunner = new Listr([
    {
      title: `Select environment ${chalk.underline(envName)} in ${chalk.underline(envType)} ${isEmpty(requestedServices) ? '' : `for service selection: ${chalk.underline(join(requestedServices, ','))}`}`,
      task: async (): Promise<void> => {
        return new Promise((resolve) => {

          // give some time to cancel
          setTimeout(() => {
            resolve();
          }, ONE_SECOND);
        })
      },
    },
    ...preTasks,
    {
      title: `Execute command for ${serviceAmount} services`,
      task: async() => {
        return new Listr(tasks, {
          ...defaultTaskOptions,
          ...taskOptions,
        });
      },
    },
    ...postTasks,
  ], {
    ...defaultTaskOptions,
    concurrent: false,
  });

  return taskRunner;
};

/**
 * The default yargs command builder.
 * @param argv 
 */
export const initializeCommand = (): void =>  {
  environment.buildEnvArgs(yargs);

  yargs.option('service', {
    describe: `Execute only for specific services`,
    type: 'string',
    default: null,
  }).alias('s', 'service');

  process.on('unhandledRejection', (error: any) => {
    console.error('Could not execute command due to an unhandled rejection:');
    console.error(error);
    process.exit(1);
  });

  process.on('uncaughtException', (error: Error) => {
    console.error('Could not execute command due to an uncaught exception:');
    console.error(error);
    process.exit(1);
  });

  process.on('SIGINT', () => {
    console.info('Caught interrupt signal. Exiting gracefully...');
    process.exit();
  });
};